import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/services/api/client';
import {
  BookOpen, Search, Scale, Bell, Gavel, GraduationCap,
  ChevronDown, ChevronUp, Clock, Play, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ==================== Mock Data ====================

const KNOWLEDGE_BASE = [
  { title: 'Concessional Contribution Cap', category: 'Contributions', description: 'The concessional contributions cap for 2025-26 is $30,000 per member, regardless of age. Includes SG, salary sacrifice, and personal deductible contributions.', sisSection: 'SIS Reg 7.04', updated: '01/07/2025' },
  { title: 'Non-Concessional Contribution Cap', category: 'Contributions', description: 'The NCC cap for 2025-26 is $120,000 per year, with a bring-forward rule allowing up to $360,000 over 3 years for members under 75.', sisSection: 'SIS s.292-85', updated: '01/07/2025' },
  { title: 'Transfer Balance Cap (TBC)', category: 'Pensions', description: 'The general TBC is $2,000,000 from 1 July 2025. Personal TBC may be lower depending on when the member first commenced a retirement phase income stream.', sisSection: 'ITAA97 Div 294', updated: '01/07/2025' },
  { title: 'Minimum Pension Drawdown Rates', category: 'Pensions', description: 'Minimum annual pension percentages based on age: under 65 (4%), 65-74 (5%), 75-79 (6%), 80-84 (7%), 85-89 (9%), 90-94 (11%), 95+ (14%).', sisSection: 'SIS Reg 1.06(9A)', updated: '01/07/2025' },
  { title: 'In-House Asset Rules', category: 'Investments', description: 'In-house assets must not exceed 5% of total fund assets at market value. Includes loans to, investments in, or assets leased to related parties.', sisSection: 'SIS s.71', updated: '15/03/2026' },
  { title: 'SMSF Annual Return (SAR)', category: 'Compliance', description: 'SMSFs must lodge a SAR each year by the due date (usually 28 February for 30 June year-end, or as per tax agent lodgement schedule).', sisSection: 'SIS s.35B', updated: '01/01/2026' },
  { title: 'Investment Strategy Requirements', category: 'Investments', description: 'Trustees must prepare and implement an investment strategy considering risk, return, diversification, liquidity, and insurance needs of members.', sisSection: 'SIS Reg 4.09', updated: '15/03/2026' },
  { title: 'Sole Purpose Test', category: 'Compliance', description: 'An SMSF must be maintained for the sole purpose of providing retirement benefits to members, or to dependants if a member dies before retirement.', sisSection: 'SIS s.62', updated: '01/01/2026' },
  { title: 'Capital Gains Tax Exemptions', category: 'Tax', description: 'Assets supporting retirement phase pensions are exempt from CGT. For unsegregated funds, the exempt proportion is determined by an actuarial certificate.', sisSection: 'ITAA97 s.118-320', updated: '15/03/2026' },
  { title: 'Trustee Obligations', category: 'Administration', description: 'Trustees must keep proper records, have a trust deed, prepare financial statements, appoint an approved auditor, and lodge the SAR on time.', sisSection: 'SIS Part 8', updated: '01/01/2026' },
];

const RULING_QUERIES = [
  {
    question: 'Can my SMSF buy property from my parents?',
    answer: 'Generally, an SMSF cannot acquire assets from related parties (SIS s.66). However, there are exceptions for business real property (used wholly and exclusively in a business) and listed securities. Residential property from related parties is prohibited. Business real property can be acquired at market value with a qualified independent valuation.',
  },
  {
    question: 'What is the current Transfer Balance Cap (TBC)?',
    answer: 'The general Transfer Balance Cap is $2,000,000 from 1 July 2025 (indexed from the original $1.6M in 2017). A member\'s personal TBC depends on when they first commenced a retirement phase income stream. Members who used some of their cap before indexation may have a proportionally adjusted personal TBC.',
  },
  {
    question: 'Can a member borrow from the fund?',
    answer: 'No. An SMSF cannot lend money or provide financial assistance to a member or relative of a member (SIS s.65). This is a strict prohibition. Any loan to a member, even if on commercial terms, would breach the SIS Act and could result in the fund being made non-complying, attracting a 45% tax rate on all assets.',
  },
  {
    question: 'What is the sole purpose test?',
    answer: 'The sole purpose test (SIS s.62) requires that an SMSF be maintained for the sole purpose of providing retirement benefits to members, or benefits to dependants on death of a member. The fund\'s assets cannot provide current day benefits to members or related parties. Breaching the sole purpose test can result in the fund losing its complying status.',
  },
];

const LEGISLATIVE_ALERTS = [
  { date: '01/07/2025', title: 'TBC Indexed to $2,000,000', description: 'The Transfer Balance Cap has been indexed from $1.9M to $2M, effective 1 July 2025.', impact: 'high' as const, effectiveDate: '01/07/2025' },
  { date: '01/07/2025', title: 'SG Rate Increase to 12%', description: 'The Superannuation Guarantee rate increases to 12% from 1 July 2025, completing the legislated increases.', impact: 'high' as const, effectiveDate: '01/07/2025' },
  { date: '01/07/2025', title: 'Concessional Cap Increase to $30,000', description: 'The concessional contributions cap increases to $30,000 (from $27,500), indexed in line with AWOTE.', impact: 'medium' as const, effectiveDate: '01/07/2025' },
  { date: '15/03/2026', title: 'Preservation Age Now 60 for All', description: 'As of 1 July 2024, preservation age is 60 for all members born after 30 June 1964. No more transitional arrangements.', impact: 'medium' as const, effectiveDate: '01/07/2024' },
  { date: '01/01/2026', title: 'Proposed: $3M+ Super Tax Changes', description: 'Draft legislation for additional 15% tax on earnings for balances above $3M. Currently before Parliament. Proposed effective date 1 July 2026.', impact: 'high' as const, effectiveDate: '01/07/2026' },
  { date: '01/11/2025', title: '6-Member SMSF Limit Now Effective', description: 'SMSFs and small APRA funds can now have up to 6 members (previously 4). Effective from 1 July 2021 legislation.', impact: 'low' as const, effectiveDate: '01/07/2021' },
];

const CASE_LAW = [
  {
    name: 'Commissioner of Taxation v Bamford [2010] HCA 10',
    year: 2010,
    topic: 'Trust Income / Proportionate Approach',
    outcome: 'High Court held that trust income is determined by the trust deed, not tax law. Proportionate approach applies for distributing income.',
    relevance: 'Critical for understanding how SMSF trust income is calculated and distributed. Affects pension calculations and tax-exempt income proportions.',
  },
  {
    name: 'Re Marsella [2019] VSC 65 (Hart Case)',
    year: 2019,
    topic: 'Sole Purpose Test / BDBN',
    outcome: 'Court upheld a binding death benefit nomination directing benefits to a legal personal representative, even where beneficiary was estranged.',
    relevance: 'Reinforces the importance of valid BDBNs. Trustees must follow valid nominations regardless of personal views on appropriateness.',
  },
  {
    name: 'JNVQ and Commissioner of Taxation [2023] AATA',
    year: 2023,
    topic: 'LRBA / In-House Assets',
    outcome: 'Tribunal found that a related party LRBA that did not comply with safe harbour terms (PCG 2016/5) resulted in the asset being treated as an in-house asset.',
    relevance: 'Highlights the importance of complying with PCG 2016/5 for LRBAs. Non-compliance can trigger in-house asset breaches and administrative penalties.',
  },
  {
    name: 'Aussiegolfa Pty Ltd v Commissioner of Taxation [2018] FCAFC 122',
    year: 2018,
    topic: 'In-House Assets / Unit Trusts',
    outcome: 'Full Federal Court held that units in a unit trust were not in-house assets where the SMSF did not have a controlling interest and the trust was not a related trust.',
    relevance: 'Important for SMSFs investing in unit trusts and determining whether such investments constitute in-house assets under SIS s.71.',
  },
];

const EDUCATION_MODULES = [
  { title: 'Trustee Responsibilities', description: 'Understanding the duties and obligations of being an SMSF trustee, including fiduciary duties and regulatory requirements.', time: '45 min', completion: 100 },
  { title: 'Investment Strategy', description: 'How to develop, implement, and review an investment strategy that meets SIS requirements and member needs.', time: '60 min', completion: 75 },
  { title: 'Contribution Rules', description: 'Comprehensive overview of contribution types, caps, carry-forward rules, and work test requirements.', time: '50 min', completion: 60 },
  { title: 'Pension Rules', description: 'Account-based pensions, transition to retirement, minimum drawdown rates, and transfer balance cap.', time: '55 min', completion: 30 },
  { title: 'Record Keeping', description: 'Required records, retention periods, financial statements, and documentation best practices.', time: '30 min', completion: 0 },
  { title: 'Insurance in Super', description: 'Types of insurance available in super, duty to consider insurance, and claims management.', time: '40 min', completion: 0 },
];

const CATEGORIES = ['All', 'Contributions', 'Pensions', 'Investments', 'Compliance', 'Tax', 'Administration'];

// ==================== Component ====================

export default function SMSFKnowledgePage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedRuling, setExpandedRuling] = useState<number | null>(null);
  const [expandedCase, setExpandedCase] = useState<number | null>(null);

  // --- API Query (minimal: fund count for context) ---
  const { data: fundsData, isLoading: fundsLoading } = useQuery({
    queryKey: ['smsf-funds'],
    queryFn: async () => { const r = await apiClient.get('/smsf/funds'); return r.data.data; },
    staleTime: 5 * 60 * 1000,
  });

  const fundCount = fundsData?.length ?? 0;

  const filteredKB = KNOWLEDGE_BASE.filter(item => {
    const matchesSearch = !searchTerm || item.title.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      <PageHeader icon={BookOpen} title={`SMSF Knowledge & Education${fundCount > 0 ? ` (${fundCount} funds)` : ''}`} />

      {fundsLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      <Tabs defaultValue="knowledge-base">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="knowledge-base"><Search className="h-3.5 w-3.5 mr-1.5" />Knowledge Base</TabsTrigger>
          <TabsTrigger value="ruling-assistant"><Scale className="h-3.5 w-3.5 mr-1.5" />Ruling Assistant</TabsTrigger>
          <TabsTrigger value="legislative-alerts"><Bell className="h-3.5 w-3.5 mr-1.5" />Legislative Alerts</TabsTrigger>
          <TabsTrigger value="case-law"><Gavel className="h-3.5 w-3.5 mr-1.5" />Case Law</TabsTrigger>
          <TabsTrigger value="trustee-education"><GraduationCap className="h-3.5 w-3.5 mr-1.5" />Trustee Education</TabsTrigger>
        </TabsList>

        {/* ===== Knowledge Base ===== */}
        <TabsContent value="knowledge-base" className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search SMSF topics..."
                className="pl-9"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-1 flex-wrap">
              {CATEGORIES.map(cat => (
                <Button
                  key={cat}
                  size="sm"
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  className="h-8 text-xs"
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {filteredKB.map((item, i) => (
              <Card key={i}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between mb-1">
                    <p className="text-sm font-semibold">{item.title}</p>
                    <Badge variant="secondary">{item.category}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{item.description}</p>
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                    <span>Ref: {item.sisSection}</span>
                    <span>Updated: {item.updated}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            {filteredKB.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">No results found for "{searchTerm}"</p>
            )}
          </div>
        </TabsContent>

        {/* ===== Ruling Assistant ===== */}
        <TabsContent value="ruling-assistant" className="space-y-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <p className="text-sm font-semibold mb-2">Ask about a specific SMSF ruling</p>
              <div className="flex gap-2">
                <Input placeholder="e.g., Can my SMSF invest in cryptocurrency?" className="flex-1" />
                <Button size="sm" onClick={() => toast.info('AI ruling assistant coming soon')}>
                  <Search className="h-4 w-4 mr-1.5" />Ask
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Common Queries</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {RULING_QUERIES.map((q, i) => (
                <div key={i} className="border rounded-lg">
                  <button
                    className="w-full flex items-center justify-between p-3 text-left text-sm font-medium hover:bg-muted/50 transition-colors"
                    onClick={() => setExpandedRuling(expandedRuling === i ? null : i)}
                  >
                    <span>{q.question}</span>
                    {expandedRuling === i ? <ChevronUp className="h-4 w-4 shrink-0 ml-2" /> : <ChevronDown className="h-4 w-4 shrink-0 ml-2" />}
                  </button>
                  {expandedRuling === i && (
                    <div className="px-3 pb-3 text-xs text-muted-foreground border-t pt-2">
                      {q.answer}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Legislative Alerts ===== */}
        <TabsContent value="legislative-alerts" className="space-y-4">
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
            <div className="space-y-4">
              {LEGISLATIVE_ALERTS.map((alert, i) => (
                <div key={i} className="relative pl-10">
                  <div className={cn(
                    'absolute left-2.5 top-3 h-3 w-3 rounded-full border-2 border-background',
                    alert.impact === 'high' ? 'bg-red-500' : alert.impact === 'medium' ? 'bg-amber-500' : 'bg-blue-500'
                  )} />
                  <Card>
                    <CardContent className="pt-3 pb-3">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <p className="text-sm font-semibold">{alert.title}</p>
                          <p className="text-[11px] text-muted-foreground">{alert.date}</p>
                        </div>
                        <Badge variant={alert.impact === 'high' ? 'destructive' : alert.impact === 'medium' ? 'secondary' : 'outline'}>
                          {alert.impact.charAt(0).toUpperCase() + alert.impact.slice(1)} Impact
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{alert.description}</p>
                      <p className="text-[11px] text-muted-foreground mt-2">Effective: {alert.effectiveDate}</p>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ===== Case Law ===== */}
        <TabsContent value="case-law" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Key SMSF Cases</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 pr-4 font-medium">Case Name</th>
                      <th className="pb-2 pr-4 font-medium text-center">Year</th>
                      <th className="pb-2 pr-4 font-medium">Topic</th>
                      <th className="pb-2 pr-4 font-medium">Outcome</th>
                      <th className="pb-2 font-medium text-center">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CASE_LAW.map((c, i) => (
                      <>
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2.5 pr-4 text-xs font-medium">{c.name}</td>
                          <td className="py-2.5 pr-4 text-center">{c.year}</td>
                          <td className="py-2.5 pr-4">
                            <Badge variant="secondary">{c.topic}</Badge>
                          </td>
                          <td className="py-2.5 pr-4 text-xs text-muted-foreground max-w-xs">{c.outcome}</td>
                          <td className="py-2.5 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => setExpandedCase(expandedCase === i ? null : i)}
                            >
                              {expandedCase === i ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </Button>
                          </td>
                        </tr>
                        {expandedCase === i && (
                          <tr key={`${i}-detail`} className="border-b last:border-0 bg-muted/30">
                            <td colSpan={5} className="py-3 px-4">
                              <p className="text-xs font-semibold mb-1">Relevance</p>
                              <p className="text-xs text-muted-foreground">{c.relevance}</p>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Trustee Education ===== */}
        <TabsContent value="trustee-education" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {EDUCATION_MODULES.map((mod, i) => (
              <Card key={i}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-start justify-between mb-2">
                    <GraduationCap className="h-5 w-5 text-[#5048e5] shrink-0" />
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {mod.time}
                    </div>
                  </div>
                  <p className="text-sm font-semibold mb-1">{mod.title}</p>
                  <p className="text-xs text-muted-foreground mb-3">{mod.description}</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{mod.completion}%</span>
                    </div>
                    <Progress value={mod.completion} className="h-2" />
                  </div>
                  <Button
                    size="sm"
                    variant={mod.completion === 100 ? 'outline' : 'default'}
                    className="w-full mt-3 h-8 text-xs"
                    onClick={() => toast.info(`${mod.completion === 100 ? 'Review' : 'Start'}: ${mod.title}`)}
                  >
                    <Play className="h-3 w-3 mr-1" />
                    {mod.completion === 100 ? 'Review Module' : mod.completion > 0 ? 'Continue Module' : 'Start Module'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
